import { useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import type Policy from 'modules/payroll_bundle/store/models/Policy'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

import OpenPeriodModal from 'modules/payroll_bundle/components/PolicyPeriodNavigator/OpenPeriodModal'
import PolicyPeriodNavigator from 'modules/payroll_bundle/components/PolicyPeriodSelector/PolicyPeriodNavigator'
import PolicyPeriodPopoverContent from 'modules/payroll_bundle/components/PolicyPeriodSelector/PolicyPeriodPopoverContent'

interface CollaborateWidgetProps {
  policy: Policy
  policyPeriods: PolicyPeriod[]
  selectedPolicyPeriod: PolicyPeriod
  onPolicyPeriodChange: (policyPeriodId: number) => void
}

export default function PolicyPeriodDropdownSelector({
  policy,
  policyPeriods,
  selectedPolicyPeriod,
  onPolicyPeriodChange,
}: CollaborateWidgetProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [openingNext, setOpeningNext] = useState(true)

  const { periodIndex, createNextPeriodForPolicy, createPreviousPeriodForPolicy } =
    usePolicyPeriods()

  function handlePopoverToggle(newPopoverState: boolean) {
    setIsPopoverOpen(newPopoverState)
  }

  const handleOpenPeriodConfirm = async (closePortal: ClosePortalFn) => {
    closePortal()
    setIsPopoverOpen(true)

    if (!policy.id) {
      return
    }

    await asyncAction(
      () =>
        periodIndex === 0
          ? createPreviousPeriodForPolicy(policy.id)
          : createNextPeriodForPolicy(policy.id),
      {
        successMessage: i18n.t('payroll_bundle.policy_period.period_opened'),
      }
    )
  }

  return (
    <Box aria-label={i18n.t('payroll_bundle.accessibility.payroll_period_selector')}>
      {/* TODO: Refactor this kind of selector into a reusable component */}
      <PopoverMenu open={isPopoverOpen} type="list" onClick={handlePopoverToggle}>
        <PopoverMenu.Trigger>
          <Box
            paddingLeft="s12"
            paddingRight="s8"
            paddingY="s4"
            flexDirection="row"
            alignItems="center"
            gap="s8"
            style={{
              opacity: '1',
              cursor: 'pointer',
            }}
            borderRadius={{ all: 'abs008' }}
            border={{
              all: {
                width: 's2',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Icon.Small icon="calendar" /> {/* TODO: Color warning if not in current period */}
            <Text.Normal>{selectedPolicyPeriod.get('period')}</Text.Normal>
            <Icon.Medium icon="chevron-down" />
          </Box>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="start" width="s360">
          <PolicyPeriodPopoverContent
            policyPeriodSelectedId={selectedPolicyPeriod.id}
            policyPeriods={policyPeriods}
            policy={policy}
            onChange={(value) => {
              onPolicyPeriodChange(value)
              setIsPopoverOpen(false)
            }}
          />

          <PolicyPeriodNavigator
            policyPeriodSelected={selectedPolicyPeriod}
            setIsPopoverOpen={setIsPopoverOpen}
            setOpeningNext={setOpeningNext}
            setOpenModal={setOpenModal}
          />
        </PopoverMenu.Content>
      </PopoverMenu>
      {/* TODO: Add a link to return to current period if not in current period */}

      {openModal && selectedPolicyPeriod?.id && (
        <OpenPeriodModal
          onConfirm={handleOpenPeriodConfirm}
          onClose={() => {
            setOpenModal(false)
            setIsPopoverOpen(true)
          }}
          policy={policy}
          openingNext={openingNext}
        />
      )}
    </Box>
  )
}
